import { IoCheckmarkSharp, IoCheckmarkDoneSharp } from "react-icons/io5";
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { BiCamera } from "react-icons/bi";
import { RiAttachment2 } from "react-icons/ri";
import { useSelector } from 'react-redux';
import { compressImages } from '../../utils/imageUtils';
import MessageImages from '../../components/MessageImages';

const API_URL = process.env.REACT_APP_API_URL;
const SentEmails = () => {
  const [sentMessages, setSentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedUser, setSelectedUser] = useState('');
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [uniqueUsers, setUniqueUsers] = useState([]);

  // Bulk messaging state
  const [showBulkMessageModal, setShowBulkMessageModal] = useState(false);
  const [bulkMessage, setBulkMessage] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  // Image upload state
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [isCompressingImages, setIsCompressingImages] = useState(false);

  // Emoji picker data
  const emojis = [
    '😊', '😄', '😍', '🥰', '😘', '😉', '😋', '😎', '🤗', '🤩',
    '👍', '👎', '👌', '✌️', '🤞', '👏', '🙌', '🤝', '💪', '👊',
    '❤️', '💖', '💕', '💗', '💓', '💝', '💘', '💞', '💟', '💌',
    '🌹', '🌸', '🌺', '🌷', '🌼', '🌻', '💐', '🌱', '🌿', '🍀',
    '🎉', '🎊', '🎈', '🎂', '🎁', '🎀', '🎪', '🎭', '🎨', '🎬',
    '☀️', '🌙', '⭐', '🌟', '✨', '💫', '🌈', '☁️', '🌤️', '🌅'
  ];

  useEffect(() => {
    // Prefill from Redux cache for faster initial paint: user-sent are NOT from admin
    if (cachedNonAdminSent.length > 0) {
      setSentMessages(cachedNonAdminSent);
      setLoading(false);
    }
    fetchSentMessages();

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use Redux cache: messages sent not by admin (i.e., by users)
  const cachedPages = useSelector(state => state.messages?.pagesByKey || {});
  const cachedNonAdminSent = useMemo(() => {
    try {
      const all = Object.values(cachedPages).map(p => p?.messages || []).flat();
      return all.filter(m => m?.from_userID?.role !== 'admin');
    } catch (_) { return []; }
  }, [cachedPages]);

  useEffect(() => {
    // Extract unique users from messages
    const users = [...new Set(sentMessages.map(msg => msg.to_userID?._id))].filter(Boolean);
    const userDetails = users.map(userId => {
      const message = sentMessages.find(msg => msg.to_userID?._id === userId);
      return {
        id: userId,
        name: message?.to_userID?.name || message?.to_userID?.userID || '不明',
        email: message?.to_userID?.email || '',
        userID: message?.to_userID?.userID || ''
      };
    });
    setUniqueUsers(userDetails);

    // Filter messages based on selected user
    if (selectedUser === '') {
      setFilteredMessages(sentMessages);
    } else {
      const filtered = sentMessages.filter(msg => msg.to_userID?._id === selectedUser);
      setFilteredMessages(filtered);
    }
  }, [sentMessages, selectedUser]);

  const fetchSentMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/messages/sent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSentMessages(response.data);
    } catch (error) {
      console.error('Error fetching sent messages:', error);
      alert('送信済みメッセージの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };


  const getReadStatusIcon = (isRead) => {
    if (isRead) {
      return <IoCheckmarkDoneSharp />; // Two tick marks for read messages
    } else {
      return <IoCheckmarkSharp />; // One tick mark for unread messages
    }
  };

  const getReadStatusColor = (isRead) => {
    return isRead ? '#28a745' : '#6c757d'; // Green for read, gray for unread
  };

  // Bulk messaging functions
  const handleBulkMessage = () => {
    setShowBulkMessageModal(true);
    setBulkMessage('');
    setSelectedUsers([]);
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const selectAllUsers = () => {
    setSelectedUsers(uniqueUsers.map(user => user.id));
  };

  const deselectAllUsers = () => {
    setSelectedUsers([]);
  };

  const addEmoji = (emoji) => {
    setBulkMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/') && !file.type.startsWith('video/'));
    
    if (imageFiles.length === 0) {
      alert('画像ファイルを選択してください（動画ファイルは除く）');
      return;
    }

    if (selectedImages.length + imageFiles.length > 10) {
      alert('画像は最大10枚まで選択できます');
      return;
    }

    setIsCompressingImages(true);
    try {
      const compressedImages = await compressImages(imageFiles);
      setSelectedImages(prev => [...prev, ...compressedImages]);
      setImagePreviewUrls(prev => [...prev, ...compressedImages]);
    } catch (error) {
      console.error('Error compressing images:', error);
      alert('画像の圧縮に失敗しました');
    } finally {
      setIsCompressingImages(false);
    }
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleViewDetail = (msg) => {
    setSelectedMessage(msg);
    setShowDetailModal(true);
  };

  const sendBulkMessage = async () => {
    if (!bulkMessage.trim() && selectedImages.length === 0) {
      alert('メッセージまたは画像を入力してください');
      return;
    }

    if (selectedUsers.length === 0) {
      alert('送信先のユーザーを選択してください');
      return;
    }

    try {
      setSendingBulk(true);
      const token = localStorage.getItem('token');

      // Deduct points for each recipient before sending
      try {
        const pointCheckResponse = await axios.get(`${API_URL}/api/point-costs`);
        const sendMessageCost = pointCheckResponse.data.find(cost => cost.actionName === 'send_message');
        const costPerMessage = sendMessageCost?.pointCost ?? 0;
        const totalCost = costPerMessage * selectedUsers.length;

        if (costPerMessage > 0) {
          // Check if user has enough points for all messages
          const deductResponse = await axios.post(`${API_URL}/api/users/deduct-points`, {
            actionName: 'send_message',
            quantity: selectedUsers.length // Send quantity for bulk deduction
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (!deductResponse.data?.success) {
            alert(`ポイントが不足しています。\n必要ポイント: ${totalCost}pt\n所持ポイント: ${deductResponse.data?.available ?? ''}pt`);
            return;
          }

          // Notify global listener to update points in sidebar
          try {
            window.dispatchEvent(new CustomEvent('points:deducted', { detail: { remainingPoints: deductResponse.data.remainingPoints } }));
          } catch (_) { }
        }
      } catch (err) {
        console.error('Error during point deduction:', err);
        alert(err?.response?.data?.message || 'ポイントの確認/減算に失敗しました');
        return;
      }

      // Send messages to all selected users
      const promises = selectedUsers.map(userId =>
        axios.post(`${API_URL}/api/messages/recipientId`, {
          userId: userId,
          content: bulkMessage,
          images: selectedImages
        }, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      await Promise.all(promises);
      alert(`${selectedUsers.length}人のユーザーにメッセージを送信しました`);
      setShowBulkMessageModal(false);
      setBulkMessage('');
      setSelectedImages([]);
      setImagePreviewUrls([]);
      setSelectedUsers([]);
      fetchSentMessages(); // Refresh the sent messages list
    } catch (error) {
      console.error('Error sending bulk message:', error);
      alert('一括メッセージの送信に失敗しました');
    } finally {
      setSendingBulk(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>読み込み中...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '10px' : '0px' }}>
      <h2 style={{
        color: '#333',
        marginBottom: '20px',
        fontSize: isMobile ? '20px' : '24px'
      }}>送信済みメッセージ</h2>

      {/* User Search Dropdown */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            style={{
              padding: isMobile ? '10px' : '12px',
              border: '1px solid #ddd',
              borderRadius: '5px',
              fontSize: isMobile ? '14px' : '16px',
              minWidth: isMobile ? '250px' : '300px',
              backgroundColor: 'white'
            }}
          >
            <option value="">すべてのユーザー</option>
            {uniqueUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {/* {user.name} {user.email ? `(${user.email})` : ''} */}
                {user.name}
              </option>
            ))}
          </select>
          <div style={{ color: '#666', fontSize: isMobile ? '12px' : '14px' }}>
            {selectedUser === '' ?
              `全${sentMessages.length}件` :
              `選択されたユーザー: ${filteredMessages.length}件`
            }
          </div>
          <button
            onClick={handleBulkMessage}
            style={{
              backgroundColor: '#ff6b35',
              color: 'white',
              border: 'none',
              padding: isMobile ? '8px 12px' : '10px 15px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: isMobile ? '12px' : '14px',
              marginLeft: '10px'
            }}
          >
            一括メッセージ送信
          </button>
        </div>
      </div>

      {sentMessages.length === 0 ? (
        <div style={{
          backgroundColor: 'white',
          padding: isMobile ? '20px' : '40px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: isMobile ? '16px' : '18px', color: '#666' }}>送信済みメッセージはありません</p>
        </div>
      ) : (
        <div style={{
          backgroundColor: 'white',
          padding: isMobile ? '15px' : '20px',
          borderRadius: '8px'
        }}>
          <h3 style={{
            marginBottom: '20px',
            fontSize: isMobile ? '16px' : '18px'
          }}>送信済みメッセージ ({sentMessages.length}件)</h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {filteredMessages.map((msg) => (
              <div
                key={msg._id}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: isMobile ? '15px' : '20px',
                  backgroundColor: '#f8f9fa'
                }}
              >
                {/* Recipient Info */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '10px' : '15px',
                  marginBottom: '15px',
                  flexWrap: isMobile ? 'wrap' : 'nowrap'
                }}>
                  {msg.to_userID?.photoLink ? (
                    <img
                      src={msg.to_userID.photoLink.photodatas || msg.to_userID.photoLink}
                      alt="Profile"
                      style={{
                        width: isMobile ? '35px' : '40px',
                        height: isMobile ? '35px' : '40px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        flexShrink: 0
                      }}
                    />
                  ) : (
                    <div style={{
                      width: isMobile ? '35px' : '40px',
                      height: isMobile ? '35px' : '40px',
                      borderRadius: '50%',
                      backgroundColor: '#ddd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: isMobile ? '10px' : '12px',
                      flexShrink: 0
                    }}>
                      なし
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: isMobile ? '14px' : '16px',
                      marginBottom: '2px'
                    }}>
                      {msg.to_userID?.name || 'Unknown'}
                    </div>
                    <div style={{
                      color: '#666',
                      fontSize: isMobile ? '12px' : '14px'
                    }}>
                      ID: {msg.to_userID?.userID || 'N/A'}
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    color: getReadStatusColor(msg.is_read),
                    fontSize: isMobile ? '14px' : '16px',
                    flexShrink: 0
                  }}>
                    {getReadStatusIcon(msg.is_read)}
                    <span style={{
                      fontSize: isMobile ? '12px' : '14px',
                      color: getReadStatusColor(msg.is_read)
                    }}>
                      {msg.is_read ? '既読' : '未読'}
                    </span>
                  </div>
                </div>

                {/* Message Content */}
                <div
                  style={{ marginBottom: '15px', cursor: 'pointer' }}
                  onClick={() => handleViewDetail(msg)}
                >
                  <div style={{
                    lineHeight: '1.5',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    fontSize: isMobile ? '14px' : '16px',
                    maxHeight: '4.5em',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {msg.body}
                    {msg.body && msg.body.length > 100 && (
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        background: 'linear-gradient(transparent, #f8f9fa)',
                        width: '50px',
                        height: '1.5em'
                      }}></div>
                    )}
                  </div>
                  {msg.images && msg.images.length > 0 && (
                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '10px' }}>
                      <MessageImages images={msg.images} style={{ maxHeight: '50px' }} />
                    </div>
                  )}
                  {/* {msg.body && msg.body.length > 100 && (
                    <div style={{
                      color: '#007bff',
                      fontSize: isMobile ? '12px' : '14px',
                      marginTop: '8px'
                    }}>
                      クリックして詳細を表示
                    </div>
                  )} */}
                </div>

                {/* Timestamp */}
                <div style={{
                  color: '#666',
                  fontSize: isMobile ? '12px' : '14px',
                  borderTop: '1px solid #eee',
                  paddingTop: '10px'
                }}>
                  送信日時: {new Date(msg.created_at).toLocaleString('ja-JP')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bulk Message Modal */}
      {showBulkMessageModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            width: isMobile ? '90%' : '600px',
            maxWidth: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>一括メッセージ送信</h3>

            {/* User Selection */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ margin: 0 }}>送信先ユーザー選択</h4>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={selectAllUsers}
                    style={{
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      padding: '5px 10px',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    全選択
                  </button>
                  <button
                    onClick={deselectAllUsers}
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '5px 10px',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    全解除
                  </button>
                </div>
              </div>

              <div style={{
                maxHeight: '200px',
                overflow: 'auto',
                border: '1px solid #ddd',
                borderRadius: '5px',
                padding: '10px'
              }}>
                {uniqueUsers.map((user) => (
                  <div key={user.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '5px 0',
                    borderBottom: '1px solid #eee'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                      style={{ marginRight: '10px' }}
                    />
                    <span style={{ fontSize: '14px' }}>{user.name} (ID: {user.userID})</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                選択済み: {selectedUsers.length}人
              </div>
            </div>

            {/* Message Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                メッセージ内容
              </label>
              <textarea
                value={bulkMessage}
                onChange={(e) => setBulkMessage(e.target.value)}
                placeholder="メッセージを入力してください..."
                style={{
                  width: '95%',
                  height: '120px',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Image Upload + Emoji controls (match ProfileSearch layout) */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <label
                    style={{
                      border: '1px solid #3498db',
                      color: 'black',
                      padding: '8px 10px 0px 10px ',
                      borderRadius: '5px',
                      cursor: (isCompressingImages || selectedImages.length >= 10) ? 'not-allowed' : 'pointer',
                      fontSize: '20px',
                      display: 'inline-block',
                      opacity: (isCompressingImages || selectedImages.length >= 10) ? 0.6 : 1
                    }}
                  >
                    <RiAttachment2 />
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageSelect}
                      disabled={isCompressingImages || selectedImages.length >= 10}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <label
                    style={{
                      border: '1px solid #27ae60',
                      color: 'black',
                      padding: '8px 10px 0px 10px ',
                      borderRadius: '5px',
                      cursor: (isCompressingImages || selectedImages.length >= 10) ? 'not-allowed' : 'pointer',
                      fontSize: '20px',
                      display: 'inline-block',
                      opacity: (isCompressingImages || selectedImages.length >= 10) ? 0.6 : 1
                    }}
                  >
                    <BiCamera />
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleImageSelect}
                      disabled={isCompressingImages || selectedImages.length >= 10}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
                <div>
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    style={{
                      backgroundColor: '#ff9800',
                      color: 'white',
                      border: 'none',
                      padding: '8px 8px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      marginRight: '10px'
                    }}
                  >
                    😊 絵文字
                  </button>
                </div>
              </div>
              {isCompressingImages && (
                <div style={{ color: '#666', fontSize: '12px', marginBottom: '10px' }}>
                  画像を圧縮中...
                </div>
              )}
              {imagePreviewUrls.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: '10px',
                  marginTop: '10px'
                }}>
                  {imagePreviewUrls.map((url, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '5px',
                          border: '1px solid #ddd'
                        }}
                      />
                      <button
                        onClick={() => removeImage(index)}
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cost Information
            {selectedUsers.length > 0 && (
              <div style={{ 
                marginTop: '15px', 
                padding: '10px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '5px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontSize: '14px', color: '#495057' }}>
                  <strong>送信情報:</strong>
                </div>
                  <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '5px' }}>
                    送信先: {selectedUsers.length}人
                  </div>
                <div style={{ fontSize: '13px', color: '#6c757d' }}>
                  必要ポイント: 各メッセージのコスト × {selectedUsers.length}人
                </div>
              </div>
            )} */}

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowBulkMessageModal(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={sendBulkMessage}
                disabled={sendingBulk || (!bulkMessage.trim() && selectedImages.length === 0) || selectedUsers.length === 0}
                style={{
                  backgroundColor: sendingBulk ? '#ccc' : '#ff6b35',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: sendingBulk ? 'not-allowed' : 'pointer'
                }}
              >
                {sendingBulk ? '送信中...' : '送信'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Emoji Picker Modal */}
      {showEmojiPicker && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          zIndex: 1001,
          maxWidth: '400px',
          maxHeight: '300px',
          overflow: 'auto'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: '10px' }}>
            {emojis.map((emoji, index) => (
              <button
                key={index}
                onClick={() => addEmoji(emoji)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '5px',
                  borderRadius: '5px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                {emoji}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowEmojiPicker(false)}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              backgroundColor: '#e74c3c',
              color: 'white',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Detailed View Modal */}
      {showDetailModal && selectedMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>

          <div style={{
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '10px',
            width: '90%',
            maxWidth: '600px',
            height: '60vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '1px solid #eee',
              paddingBottom: '15px'
            }}>
              <h3 style={{ margin: 0, color: '#333' }}>送信メッセージ詳細</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                ✕
              </button>
            </div>
            <div style={{
              top: '0px',
              right: '0px',
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              color: selectedMessage.is_read ? '#28a745' : '#6c757d'
            }}>
              {selectedMessage.is_read ? (
                <>
                  <IoCheckmarkDoneSharp />
                  <span>既読</span>
                </>
              ) : (
                <>
                  <IoCheckmarkSharp />
                  <span>未読</span>
                </>
              )}
            </div>
            {/* Recipient Info */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}>
              {selectedMessage.to_userID?.photoLink ? (
                <img
                  src={selectedMessage.to_userID.photoLink.photodatas || selectedMessage.to_userID.photoLink}
                  alt="Profile"
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: '#ddd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>
                  なし
                </div>
              )}
              <div>
                <div style={{
                  fontWeight: 'bold',
                  fontSize: '18px',
                  marginBottom: '5px'
                }}>
                  送信先: {selectedMessage.to_userID?.name || 'Unknown'}
                </div>
                <div style={{
                  color: '#666',
                  fontSize: '14px',
                  marginBottom: '5px'
                }}>
                  ID: {selectedMessage.to_userID?.userID || 'N/A'}
                </div>
                <div style={{
                  color: '#666',
                  fontSize: '14px'
                }}>
                  送信日時: {new Date(selectedMessage.created_at).toLocaleString('ja-JP')}
                </div>
              </div>

            </div>

            {/* Message Content */}
            <div style={{
              backgroundColor: '#fff',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid #eee',
              marginBottom: '20px',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#333', flexShrink: 0 }}>メッセージ内容</h4>
              <div style={{
                lineHeight: '1.6',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                fontSize: '16px',
                color: '#333',
                overflow: 'auto',
                flex: 1,
                marginBottom: selectedMessage.images && selectedMessage.images.length > 0 ? '15px' : '0'
              }}>
                {selectedMessage.body}
                {selectedMessage.images && selectedMessage.images.length > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    <MessageImages images={selectedMessage.images} />
                  </div>
                )}
              </div>
              {/* Images are rendered inside the scrollable content area (same as other pages) */}
            </div>

            {/* Action Buttons */}
            {/* <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              flexShrink: 0
            }}>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                閉じる
              </button>
            </div> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default SentEmails; 